import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { RefreshCw, Settings2, LineChart, Pause, Play, Trash2, BellRing, AlertCircle, Clock, Wallet, ArrowDownLeft, ArrowUpRight, Tag } from 'lucide-react';
import { LimiteGauge } from './LimiteGauge';
import { EtiquetaChip } from './EtiquetaChip';
import { fmtMoney, timeAgo, intervaloLabel, iniciales, mismaEtiqueta } from './vigilanciaUtils';

const IconBtn = ({ title, onClick, children, testid, disabled, danger }) => (
  <Button
    size="icon" variant="ghost" title={title} onClick={onClick} disabled={disabled} data-testid={testid}
    className={`h-8 w-8 text-slate-500 ${danger ? 'hover:text-rose-600 hover:bg-rose-50' : 'hover:text-teal-700 hover:bg-teal-50'}`}
  >
    {children}
  </Button>
);

const hoyART = () => new Date().toLocaleDateString('es-AR', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Argentina/Buenos_Aires',
});

const FlujoDia = ({ tipo, monto, cant, fecha, testid }) => {
  const ing = tipo === 'ingresos';
  const Icon = ing ? ArrowDownLeft : ArrowUpRight;
  const dia = !fecha || fecha === hoyART() ? 'hoy' : `del ${fecha.slice(0, 5)}`;
  return (
    <div className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${ing ? 'border-emerald-100 bg-emerald-50/60' : 'border-rose-100 bg-rose-50/60'}`} data-testid={testid}>
      <div className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${ing ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{ing ? 'Ingresos' : 'Egresos'} {dia}</p>
        <p className={`text-base font-bold tabular-nums truncate ${ing ? 'text-emerald-700' : 'text-rose-700'}`} data-testid={`${testid}-monto`}>
          {monto === undefined || monto === null ? '—' : fmtMoney(monto)}
        </p>
        {cant !== undefined && cant !== null && <p className="text-[11px] text-slate-400 -mt-0.5">{cant} movimiento{cant === 1 ? '' : 's'}</p>}
      </div>
    </div>
  );
};

const estadoCls = (e) => (/habilit/i.test(e || '') ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
  : 'bg-rose-50 text-rose-700 border-rose-200');

export const VigilanciaCard = ({
  v, perms, onChequear, onEditar, onHistorial, onToggle, onEliminar, onEtiquetas, onFiltrarEtiqueta, etiquetaActiva, index = 0,
}) => {
  const d = v.ultimo_dato || {};
  const al = v.alerta_estado || {};
  const enAlerta = v.alertas_activas > 0;
  const accent = !v.activo ? 'bg-slate-300' : enAlerta ? 'bg-rose-500' : 'bg-teal-600';
  return (
    <Card
      className={`relative overflow-hidden bg-white border-slate-200 transition-shadow duration-300 hover:shadow-lg animate-in fade-in slide-in-from-bottom-2 ${enAlerta ? 'ring-1 ring-rose-200' : ''}`}
      style={{ animationDelay: `${index * 60}ms`, animationFillMode: 'backwards' }}
      data-testid={`vig-card-${v.id}`}
    >
      <div className={`h-1 ${accent}`} />
      <div className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center text-sm font-bold ${enAlerta ? 'bg-rose-100 text-rose-700' : 'bg-teal-50 text-teal-800'}`}>
            {iniciales(v.nombre)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <h3 className="text-base font-bold text-slate-900 truncate max-w-full" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`vig-nombre-${v.id}`}>
                {v.nombre || 'Cuenta sin nombre'}
              </h3>
              {d.estado && <Badge variant="outline" className={`text-[10px] ${estadoCls(d.estado)}`} data-testid={`vig-estado-${v.id}`}>{d.estado}</Badge>}
              {!v.activo && <Badge className="bg-slate-200 text-slate-700 text-[10px]">Pausada</Badge>}
              {enAlerta && (
                <Badge className="bg-rose-600 text-white text-[10px] gap-1" data-testid={`vig-alertas-badge-${v.id}`}>
                  <BellRing size={10} /> {v.alertas_activas} alerta{v.alertas_activas > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 truncate">{d.email || v.email} · CUIT {d.cuit || v.cuit}</p>
            <p className="font-mono text-[11px] text-slate-400 truncate">CVU {d.cvu || v.cvu || '—'}</p>
            {v.etiquetas?.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`vig-etiquetas-${v.id}`}>
                {v.etiquetas.map((t, i) => (
                  <EtiquetaChip key={t} texto={t} testid={`vig-etiqueta-${v.id}-${i}`}
                    active={mismaEtiqueta(t, etiquetaActiva)} onClick={() => onFiltrarEtiqueta?.(t)} />
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center -mr-2 -mt-1">
            <IconBtn title="Chequear ahora" onClick={() => onChequear(v)} disabled={v.chequeando} testid={`vig-chequear-${v.id}`}>
              <RefreshCw size={15} className={v.chequeando ? 'animate-spin text-teal-600' : ''} />
            </IconBtn>
            <IconBtn title="Evolución" onClick={() => onHistorial(v)} testid={`vig-historial-${v.id}`}><LineChart size={15} /></IconBtn>
            {perms.editar && <IconBtn title="Carteles" onClick={() => onEtiquetas(v)} testid={`vig-etiquetas-btn-${v.id}`}><Tag size={15} /></IconBtn>}
            {perms.editar && <IconBtn title="Configurar alertas" onClick={() => onEditar(v)} testid={`vig-editar-${v.id}`}><Settings2 size={15} /></IconBtn>}
            {perms.editar && (
              <IconBtn title={v.activo ? 'Pausar' : 'Reanudar'} onClick={() => onToggle(v)} testid={`vig-toggle-${v.id}`}>
                {v.activo ? <Pause size={15} /> : <Play size={15} />}
              </IconBtn>
            )}
            {perms.eliminar && <IconBtn title="Dejar de vigilar" onClick={() => onEliminar(v)} testid={`vig-eliminar-${v.id}`} danger><Trash2 size={15} /></IconBtn>}
          </div>
        </div>

        <div className={`flex items-center justify-between rounded-xl px-4 py-3 ${al.saldo ? 'bg-rose-50 border border-rose-200' : 'bg-gradient-to-r from-teal-50 to-white border border-teal-100'}`}>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            <Wallet size={14} className={al.saldo ? 'text-rose-600' : 'text-teal-700'} /> Saldo disponible
          </div>
          <p className={`text-2xl font-bold tabular-nums ${al.saldo ? 'text-rose-700' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`vig-saldo-${v.id}`}>
            {fmtMoney(d.disponible)}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FlujoDia tipo="ingresos" monto={d.ingresos_dia} cant={d.cant_ingresos_dia} fecha={d.movimientos_fecha} testid={`vig-ingresos-${v.id}`} />
          <FlujoDia tipo="egresos" monto={d.egresos_dia} cant={d.cant_egresos_dia} fecha={d.movimientos_fecha} testid={`vig-egresos-${v.id}`} />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <LimiteGauge label="Límite diario" consumido={d.consumido_diario} limite={d.limite_diario} restante={d.restante_diario}
            pct={d.pct_consumido_diario} alerta={al.diario} testid={`vig-diario-${v.id}`} />
          <LimiteGauge label="Límite mensual" consumido={d.consumido_mensual} limite={d.limite_mensual} restante={d.restante_mensual}
            pct={d.pct_consumido_mensual} alerta={al.mensual} testid={`vig-mensual-${v.id}`} />
        </div>

        {v.ultimo_error && (
          <p className="flex items-start gap-1.5 text-xs text-rose-600" data-testid={`vig-error-${v.id}`}>
            <AlertCircle size={13} className="mt-0.5 shrink-0" /> {v.ultimo_error}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 pt-3 text-[11px] text-slate-500">
          <span>Últ. movimiento <b className="text-slate-700">{d.ultimo_movimiento || '—'}</b></span>
          <span data-testid={`vig-ultimo-chequeo-${v.id}`}>{v.chequeando ? 'Chequeando en Waya…' : `Chequeado ${timeAgo(v.ultimo_chequeo)}`}</span>
          <span className="ml-auto flex items-center gap-1"><Clock size={11} /> {intervaloLabel(v.intervalo_min)}</span>
        </div>
      </div>
    </Card>
  );
};

export default VigilanciaCard;
